import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, MapPin } from 'lucide-react';
import { api } from '../../lib/api';
import type { State, Area } from '../../types';
import { useUpload } from '../../lib/useUpload';
import {
  Spinner, EmptyState, Modal, Alert, ConfirmDialog, UploadProgress,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';

interface StateForm {
  name: string;
  currency: string;
  tagline: string;
  is_featured: boolean;
  featured_order: string;
  cover_image: File | null;
}
interface AreaForm {
  name: string;
  state_id: string;
  tagline: string;
  is_featured: boolean;
  featured_order: string;
  cover_image: File | null;
}

const emptyStateForm = (): StateForm => ({
  name: '',
  currency: 'NGN',
  tagline: '',
  is_featured: true,
  featured_order: '0',
  cover_image: null,
});

const emptyAreaForm = (): AreaForm => ({
  name: '',
  state_id: '',
  tagline: '',
  is_featured: false,
  featured_order: '0',
  cover_image: null,
});

export default function Locations() {
  const [states, setStates] = useState<State[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [stateModal, setStateModal] = useState(false);
  const [editingStateId, setEditingStateId] = useState<number | null>(null);
  const [stateForm, setStateForm] = useState<StateForm>(emptyStateForm());
  const stateUpload = useUpload();
  const [stateDeleteId, setStateDeleteId] = useState<number | null>(null);

  const [areaModal, setAreaModal] = useState(false);
  const [editingAreaId, setEditingAreaId] = useState<number | null>(null);
  const [areaForm, setAreaForm] = useState<AreaForm>(emptyAreaForm());
  const areaUpload = useUpload();
  const [areaDeleteId, setAreaDeleteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get<State[]>('/states'), api.get<Area[]>('/areas')])
      .then(([s, a]) => { setStates(s); setAreas(a); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openAddState = () => {
    setError('');
    setEditingStateId(null);
    setStateForm(emptyStateForm());
    setStateModal(true);
  };

  const openEditState = (s: State) => {
    setError('');
    setEditingStateId(s.id);
    setStateForm({
      name: s.name,
      currency: s.currency,
      tagline: s.tagline || '',
      is_featured: !!s.is_featured,
      featured_order: String(s.featured_order ?? 0),
      cover_image: null,
    });
    setStateModal(true);
  };

  const saveState = async (e: React.FormEvent) => {
    e.preventDefault();
    stateUpload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('name', stateForm.name);
      fd.append('currency', stateForm.currency);
      fd.append('tagline', stateForm.tagline);
      fd.append('is_featured', stateForm.is_featured ? '1' : '0');
      fd.append('featured_order', stateForm.featured_order);
      if (stateForm.cover_image) fd.append('cover_image', stateForm.cover_image);
      if (editingStateId) {
        await stateUpload.run(`/states/${editingStateId}`, fd, 'PUT');
      } else {
        await stateUpload.run('/states', fd);
      }
      setStateModal(false);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const openAddArea = () => {
    setError('');
    setEditingAreaId(null);
    setAreaForm(emptyAreaForm());
    setAreaModal(true);
  };

  const openEditArea = (a: Area) => {
    setError('');
    setEditingAreaId(a.id);
    setAreaForm({
      name: a.name,
      state_id: String(a.state_id),
      tagline: a.tagline || '',
      is_featured: !!a.is_featured,
      featured_order: String(a.featured_order ?? 0),
      cover_image: null,
    });
    setAreaModal(true);
  };

  const saveArea = async (e: React.FormEvent) => {
    e.preventDefault();
    areaUpload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('name', areaForm.name);
      fd.append('state_id', areaForm.state_id);
      fd.append('tagline', areaForm.tagline);
      fd.append('is_featured', areaForm.is_featured ? '1' : '0');
      fd.append('featured_order', areaForm.featured_order);
      if (areaForm.cover_image) fd.append('cover_image', areaForm.cover_image);
      if (editingAreaId) {
        await areaUpload.run(`/areas/${editingAreaId}`, fd, 'PUT');
      } else {
        await areaUpload.run('/areas', fd);
      }
      setAreaModal(false);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const deleteState = async (id: number) => {
    try {
      await api.delete(`/states/${id}`);
      setStateDeleteId(null);
      load();
    } catch {
      setStateDeleteId(null);
    }
  };

  const deleteArea = async (id: number) => {
    try {
      await api.delete(`/areas/${id}`);
      setAreaDeleteId(null);
      load();
    } catch {
      setAreaDeleteId(null);
    }
  };

  if (loading) return <Spinner label="Loading locations..." />;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">States & Locations</h1>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-lg font-semibold">States</h2>
        <button onClick={openAddState} className={primaryBtn}>
          <Plus className="h-4 w-4" /> Add State
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Featured states and areas appear on the homepage and in the footer. Areas added under a state also appear
        in the site's location dropdowns.
      </p>
      {states.length === 0 ? (
        <EmptyState message="No states yet." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {states.map((s) => (
            <div key={s.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative h-32">
                {s.cover_image ? (
                  <img src={s.cover_image} alt={s.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <MapPin className="h-8 w-8 text-gray-300" />
                  </div>
                )}
                {s.is_featured && (
                  <span className="absolute top-2 right-2 bg-primary text-black text-xs font-semibold px-2 py-0.5 rounded-full">
                    Featured
                  </span>
                )}
              </div>
              <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold">{s.name}</p>
                  <p className="text-xs text-gray-500">
                    {s.tagline || 'No tagline'} · {s.currency}
                    {s.featured_order ? ` · Order ${s.featured_order}` : ''}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {areas.filter((a) => a.state_name === s.name).length} area(s)
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button onClick={() => openEditState(s)} className="p-2 rounded-lg hover:bg-primary/10 text-primary" title="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => setStateDeleteId(s.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-lg font-semibold">Areas / Locations in State</h2>
        <button onClick={openAddArea} className={primaryBtn}>
          <Plus className="h-4 w-4" /> Add Area
        </button>
      </div>
      {areas.length === 0 ? (
        <EmptyState message="No areas yet." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {areas.map((a) => (
            <div key={a.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative h-28">
                {a.cover_image ? (
                  <img src={a.cover_image} alt={a.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-gray-300" />
                  </div>
                )}
                {a.is_featured && (
                  <span className="absolute top-2 right-2 bg-primary text-black text-xs font-semibold px-2 py-0.5 rounded-full">
                    Featured
                  </span>
                )}
              </div>
              <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold">{a.name}</p>
                  <p className="text-xs text-gray-500">
                    {a.state_name}
                    {a.featured_order ? ` · Order ${a.featured_order}` : ''}
                  </p>
                  {a.tagline && <p className="text-xs text-gray-400 mt-1">{a.tagline}</p>}
                </div>
                <div className="flex space-x-2">
                  <button onClick={() => openEditArea(a)} className="p-2 rounded-lg hover:bg-primary/10 text-primary" title="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => setAreaDeleteId(a.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={stateModal} onClose={() => setStateModal(false)} title={editingStateId ? 'Edit State' : 'Add State'}>
        {error && <Alert>{error}</Alert>}
        <form onSubmit={saveState} className="space-y-4">
          <div>
            <label className={labelClass}>Name *</label>
            <input className={inputClass} required value={stateForm.name} onChange={(e) => setStateForm({ ...stateForm, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Currency</label>
              <select className={inputClass} value={stateForm.currency} onChange={(e) => setStateForm({ ...stateForm, currency: e.target.value })}>
                <option value="NGN">NGN</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Featured Order</label>
              <input type="number" min={0} className={inputClass} value={stateForm.featured_order} onChange={(e) => setStateForm({ ...stateForm, featured_order: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Catchphrase / Tagline</label>
            <input className={inputClass} value={stateForm.tagline} onChange={(e) => setStateForm({ ...stateForm, tagline: e.target.value })} placeholder="e.g. The Centre of Excellence" />
          </div>
          <div>
            <label className={labelClass}>Cover Image</label>
            <input type="file" accept="image/*" className={inputClass} onChange={(e) => setStateForm({ ...stateForm, cover_image: e.target.files?.[0] || null })} />
          </div>
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={stateForm.is_featured}
              onChange={(e) => setStateForm({ ...stateForm, is_featured: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span>Show in Featured Locations on homepage and footer</span>
          </label>
          <UploadProgress
            percent={stateUpload.percent}
            phase={stateUpload.phase}
            error={stateUpload.error}
            onCancel={stateUpload.cancel}
          />
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setStateModal(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={stateUpload.busy} className={primaryBtn}>
              {stateUpload.phase === 'uploading'
                ? `Uploading ${stateUpload.percent}%`
                : stateUpload.phase === 'processing' ? 'Saving...' : editingStateId ? 'Save Changes' : 'Add State'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={areaModal} onClose={() => setAreaModal(false)} title={editingAreaId ? 'Edit Area' : 'Add Area'}>
        {error && <Alert>{error}</Alert>}
        <form onSubmit={saveArea} className="space-y-4">
          <div>
            <label className={labelClass}>State *</label>
            <select className={inputClass} required value={areaForm.state_id} onChange={(e) => setAreaForm({ ...areaForm, state_id: e.target.value })}>
              <option value="">Select state</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Area Name *</label>
            <input className={inputClass} required value={areaForm.name} onChange={(e) => setAreaForm({ ...areaForm, name: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Catchphrase / Tagline</label>
            <input className={inputClass} value={areaForm.tagline} onChange={(e) => setAreaForm({ ...areaForm, tagline: e.target.value })} placeholder="e.g. Diplomatic enclave" />
          </div>
          <div>
            <label className={labelClass}>Featured Order</label>
            <input type="number" min={0} className={inputClass} value={areaForm.featured_order} onChange={(e) => setAreaForm({ ...areaForm, featured_order: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Cover Image</label>
            <input type="file" accept="image/*" className={inputClass} onChange={(e) => setAreaForm({ ...areaForm, cover_image: e.target.files?.[0] || null })} />
          </div>
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={areaForm.is_featured}
              onChange={(e) => setAreaForm({ ...areaForm, is_featured: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span>Show in Featured Locations on homepage and footer</span>
          </label>
          <UploadProgress
            percent={areaUpload.percent}
            phase={areaUpload.phase}
            error={areaUpload.error}
            onCancel={areaUpload.cancel}
          />
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setAreaModal(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={areaUpload.busy} className={primaryBtn}>
              {areaUpload.phase === 'uploading'
                ? `Uploading ${areaUpload.percent}%`
                : areaUpload.phase === 'processing' ? 'Saving...' : editingAreaId ? 'Save Changes' : 'Add Area'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!stateDeleteId}
        onClose={() => setStateDeleteId(null)}
        onConfirm={() => stateDeleteId && deleteState(stateDeleteId)}
        title="Delete State"
        message="Are you sure you want to delete this state? Its areas will also be removed."
      />
      <ConfirmDialog
        open={!!areaDeleteId}
        onClose={() => setAreaDeleteId(null)}
        onConfirm={() => areaDeleteId && deleteArea(areaDeleteId)}
        title="Delete Area"
        message="Are you sure you want to delete this area?"
      />
    </div>
  );
}
